import { ChevronLeft, ChevronRight, Loader2, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import type { useNavigate } from "react-router-dom";
import { useToast } from "@/components/shared/Toast";
import { useGetTeamConversations } from "@/http/queries/team/useGetTeamConversations";
import { timeAgo } from "./utils";

const PAGE_SIZE = 25;

export function ConversationsTab({
  teamUuid,
  navigate,
}: Readonly<{
  teamUuid: string;
  navigate: ReturnType<typeof useNavigate>;
}>) {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const { data, isPending, error } = useGetTeamConversations(teamUuid, { page, pageSize: PAGE_SIZE });

  useEffect(() => {
    if (!error) return;
    console.error("Failed to load team conversations:", error);
    showToast("Failed to load conversations", "error");
  }, [error, showToast]);

  const chats = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = data?.meta.totalPages ?? 0;

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-surface-secondary/50 rounded-xl p-8 text-center">
          <MessageSquare className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
          <p className="text-sm text-content-tertiary">No conversations in this team yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-5 w-5 text-content-quaternary" />
        <h2 className="text-lg font-semibold text-content-primary">Conversations</h2>
        <span className="text-sm text-content-quaternary">
          {total} {total === 1 ? "conversation" : "conversations"}
        </span>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        {chats.map((chat, idx) => (
          <button
            key={chat.uuid}
            onClick={() => navigate(`/chat/${chat.uuid}`)}
            className={`w-full flex items-center gap-4 px-5 py-3.5 hover:bg-interactive-hover transition-colors text-left ${
              idx < chats.length - 1 ? "border-b border-border-primary" : ""
            }`}
          >
            <div className="h-9 w-9 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
              <MessageSquare className="h-4 w-4 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-content-primary truncate">
                {chat.title || "Untitled conversation"}
              </p>
              <p className="text-xs text-content-quaternary mt-0.5">
                {chat.userName && `${chat.userName} · `}
                {chat.agentName && `with ${chat.agentName} · `}
                {chat.messageCount} messages · {timeAgo(chat.updatedAt)}
              </p>
            </div>
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-8 pt-4 border-t border-border-primary">
          <span className="text-sm text-content-tertiary">
            Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-border-primary text-content-tertiary hover:bg-interactive-hover disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-4 h-4" />
              Prev
            </button>
            <span className="text-sm text-content-secondary px-2">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-border-primary text-content-tertiary hover:bg-interactive-hover disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
